import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import Button from 'components/helpers/Button';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { TextWithOptionalLineBreaks } from 'components/helpers/Heading';
import HeadingTag from 'components/helpers/HeadingTag';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { classnames } from 'tailwindcss-classnames';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { getEnumValue } from 'lib/util/enum';
import { ButtonVariant } from 'components/helpers/Button/WithButton';
import { useIsNRC } from 'lib/util/site-configuration';

export type HomepageHeroProps = Sitecore.Override.ComponentBase &
  Components.Heroes.HomepageHero.Fields.HomepageHero;

const lgFlexRow = `lg:flex-row`;

const textCenter = `text-center`;

const HomepageHero = ({ fields }: HomepageHeroProps): JSX.Element => {
  const isNrc = useIsNRC();
  const bgColor = isNrc ? `bg-white` : `bg-blue-navy`;
  const textColor = isNrc ? `text-blue-navy` : `text-white`;
  const textBlueColor = `text-blue-navy`;
  const isExperienceEditor = useExperienceEditor();
  const isFullBackground = getEnumValue(fields?.heroVariations) === `full`;
  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }
  let lines;
  if (!fields?.numberOfLines) {
    lines = 4;
  } else {
    lines = parseInt(getEnumValue<string>(fields?.numberOfLines), 10);
  }
  const numberOfLines = classnames({
    'line-clamp-1': lines === 1,
    'line-clamp-2': lines === 2,
    'line-clamp-3': lines === 3,
    'line-clamp-4': lines === 4,
    'line-clamp-5': lines === 5,
  });

  const cta2TypeField = fields.cta2Type.value;
  const cta2Type: ButtonVariant =
    cta2TypeField === 'Default' || cta2TypeField.trim().length === 0
      ? 'secondary'
      : (cta2TypeField.toLowerCase() as ButtonVariant);

  // HACK: override image width to 1024px so it responds correctly
  const nextImageField = {
    ...fields.image,
    value: isFullBackground
      ? { ...fields.image.value }
      : {
          ...fields.image.value,
          width: `1024`,
          height: `576`,
        },
  };

  return (
    <>
      <div
        className={classnames('overflow-hidden')}
        data-component="authorable/Heroes/homepagehero"
      >
        {getEnumValue(fields.heroVariations) !== 'full' && (
          <div
            className={classnames(
              'overflow-hidden',
              'relative',
              'max-w-screen-2xl',
              'w-full',
              'relative',
              'flex',
              'flex-col',
              'mx-auto',
              lgFlexRow,
              'lg:flex-row-reverse',
              'lg:min-w-[730px]'
            )}
          >
            <div
              className={classnames(
                'flex',
                'justify-end',
                'h-auto',
                'w-full',
                'relative',
                'lg:w-8/12'
              )}
            >
              <ImageWrapper
                className={classnames(
                  'min-h-[250px]',
                  'min-w-[442.5px]',
                  'lg:h-full',
                  'object-cover'
                )}
                field={nextImageField}
              />
              {getEnumValue(fields.heroVariations) === 'rounded' ? (
                <>
                  <span
                    className={classnames(
                      'rounded-full',
                      bgColor,
                      'absolute',
                      'block',
                      'overflow-x-hidden',
                      'h-[300%]',
                      'w-[200%]',
                      'top-[82%]',
                      'left-[-50%]',
                      'lg:left-[-240%]',
                      'lg:h-[250%]',
                      'lg:w-[250%]',
                      'lg:top-[-74%]',
                      'lg:overflow-y-hidden'
                    )}
                  />
                </>
              ) : (
                <></>
              )}
            </div>
            <div className={classnames('w-full', bgColor, 'z-10', 'lg:w-5/12')}>
              <div
                className={classnames(
                  'flex',
                  'flex-col',
                  'h-full',
                  'p-6',
                  'w-full',
                  'flex-col',
                  'justify-center',
                  'xl:pl-20',
                  'xl:pr-20',
                  bgColor,
                  'lg:bg-opacity-0'
                )}
              >
                <div>
                  <HeadingTag
                    headingLevel="h1"
                    className={classnames(textColor, 'mb-2', 'heading-xl', 'line-clamp-2')}
                  >
                    <TextWithOptionalLineBreaks tag="" field={fields.heroHeadline} />
                  </HeadingTag>
                  <RichTextA11yWrapper
                    className={classnames(textColor, 'mb-2', 'text-body', `${numberOfLines}`)}
                    field={fields.description}
                  />
                </div>
                {isExperienceEditor || fields.cta1.value.href || fields.cta2.value.href ? (
                  <div className={classnames('flex', 'flex-col', 'pt-6', 'w-full')}>
                    {fields?.useLinkButton.value ? (
                      <>
                        <LinkA11yWrapper
                          className={classnames('underline', 'p-2.5', textColor)}
                          editable={false}
                          field={fields.cta1}
                        />
                        <LinkA11yWrapper
                          className={classnames('underline', 'p-2.5', textColor)}
                          editable={false}
                          field={fields.cta2}
                        />
                      </>
                    ) : (
                      <>
                        {isNrc ? (
                          <div className={classnames('flex', 'flex-col')}>
                            <Button
                              linkField={fields.cta1}
                              className={classnames(textCenter, 'lg:mr-2', 'lg:mr-6', 'mb-2')}
                            />
                            <Button
                              linkField={fields.cta2}
                              className={classnames(textCenter, 'mt-4', 'lg:mt-0')}
                              variant={cta2Type}
                            />
                          </div>
                        ) : (
                          <>
                            <Button linkField={fields.cta1} className={classnames(textCenter)} />
                            <Button
                              linkField={fields.cta2}
                              className={classnames(textCenter, 'mt-4')}
                              variant={cta2Type}
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
        )}
        {getEnumValue(fields.heroVariations) === 'full' && (
          <div
            className={classnames(
              'w-full',
              'relative',
              'max-w-screen-2xl',
              'relative',
              'mx-auto',
              'lg:min-w-[730px]',
              'flex',
              'lg:block',
              'flex-col-reverse'
            )}
          >
            <div
              className={classnames(
                'w-full',
                'h-full',
                'flex',
                'flex-col',
                'lg:absolute',
                'justify-center',
                'items-start',
                'z-10',
                'lg:left-20'
              )}
            >
              <div className={classnames('w-full', 'bg-white', 'z-10', 'lg:w-5/12', 'rounded-lg')}>
                <div className={classnames('h-full', 'p-10', 'w-full', 'bg-white', 'rounded-lg')}>
                  <div>
                    <HeadingTag
                      headingLevel="h1"
                      className={classnames(textBlueColor, 'mb-2', 'heading-lg', 'line-clamp-2')}
                    >
                      <TextWithOptionalLineBreaks tag="" field={fields.heroHeadline} />
                    </HeadingTag>
                    <RichTextA11yWrapper
                      className={classnames(
                        'text-gray-brightGray',
                        'mb-2',
                        'text-body',
                        `${numberOfLines}`
                      )}
                      field={fields.description}
                    />
                  </div>
                  {isExperienceEditor || fields.cta1.value.href || fields.cta2.value.href ? (
                    <div className={classnames('flex', 'flex-col', 'pt-6', 'w-full')}>
                      {fields?.useLinkButton.value ? (
                        <>
                          <LinkA11yWrapper
                            className={classnames('underline', 'p-2.5', textBlueColor)}
                            editable={false}
                            field={fields.cta1}
                          />
                          <LinkA11yWrapper
                            className={classnames('underline', 'p-2.5', textBlueColor)}
                            editable={false}
                            field={fields.cta2}
                          />
                        </>
                      ) : (
                        <>
                          <Button linkField={fields.cta1} className={classnames(textCenter)} />
                          <Button
                            linkField={fields.cta2}
                            className={classnames(textCenter, 'mt-4')}
                            variant={cta2Type}
                          />
                        </>
                      )}
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </div>
            <div className={classnames('h-auto', 'w-full', 'relative')}>
              <ImageWrapper
                priority
                className={classnames('min-h-[250px]', 'min-w-[442.5px]', 'lg:h-full')}
                field={nextImageField}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HomepageHero;
